import { useEffect, useRef, useState } from 'react';
import { PrismicLink } from 'prismic/types/link';
import { PrismicRichText } from 'prismic/types/richtext';
import { linkResolver } from 'prismic/linkResolver';

import { RichText } from 'components/rich-text/RichText';
import { GridLayout, GridLayoutItem } from 'components/grid/GridLayout';
import { Button } from 'components/button/Button';

import s from './PromoBanner.module.scss';
import cx from 'classnames';

import { colors } from './colors';

interface PromoBannerProps {
  title: string;
  content: PrismicRichText;
  linkText?: string;
  linkTo?: PrismicLink;
  colorTheme: string;
}

export const PromoBanner = ({ ...props }: PromoBannerProps) => {
  const titleRef = useRef<HTMLDivElement>(null);

  const { title, content, linkText, linkTo, colorTheme = 'purple' } = props;

  const [titleCols, setTitleCols] = useState(6);
  const [textCols, setTextCols] = useState(6);

  const backgroundColor = colors[colorTheme]?.bg || colors['purple'].bg;
  const textColor = colors[colorTheme]?.text || colors['purple'].text;

  const themeStyles = {
    '--banner-background-color': backgroundColor,
    '--banner-content-color': textColor,
  } as React.CSSProperties;

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--banner-background-color',
      backgroundColor
    );
  }, [backgroundColor]);

  useEffect(() => {
    if (titleRef?.current !== undefined && titleRef?.current !== null) {
      const width = titleRef.current.clientWidth;
      const colWidth = titleRef.current.parentElement?.scrollWidth
        ? titleRef.current.parentElement.scrollWidth / 12
        : 100;

      if (width < 1 * colWidth) {
        setTitleCols(1);
        setTextCols(8);
      } else if (width < 2 * colWidth) {
        setTitleCols(2);
        setTextCols(7);
      } else if (width < 3 * colWidth) {
        setTitleCols(3);
        setTextCols(6);
      } else {
        setTitleCols(4);
        setTextCols(5);
      }
    }
  }, [title]);

  return (
    <div style={themeStyles}>
      {/* Used to calculate the width of the title without the grid interfering */}
      <div ref={titleRef} className={s.hidden} aria-hidden>
        {title}
      </div>
      <GridLayout className={s.background}>
        <GridLayoutItem
          offset={{ desktop: 1 }}
          columns={{ desktop: titleCols }}
        >
          {title && (
            <h1 className={cx(s.title, { [s.nowrap]: titleCols < 4 })}>
              {title}
            </h1>
          )}
        </GridLayoutItem>
        <GridLayoutItem
          offset={{ desktop: titleCols + 2 }}
          columns={{ desktop: textCols }}
        >
          <>
            {content && <RichText className={s.content}>{content}</RichText>}
            {linkTo && (
              <Button
                style="simple"
                to={linkResolver(linkTo)}
                icon="arrowright"
                className={s.button}
              >
                {linkText ?? 'Skoða nánar'}
              </Button>
            )}
          </>
        </GridLayoutItem>
      </GridLayout>
    </div>
  );
};
